import { TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';

import TableWrapper from '@/components/atoms/TableWrapper';
import CustomTable from '@/components/atoms/CustomTable';
import { Filters } from './Filters';
import {
  setOrdersFilters,
  useOrdersFilters,
} from '@/store/slices/reportFiltersSlice';
import { RequestDetailsDrawer } from './RequestDetailsDrawer';
import { useGetOrdersReportQuery } from '@/store/services/reports';
import {
  setModalId,
  setPage,
  usePublicState,
} from '@/store/slices/publicSlice';

export interface OrderReportType {
  orderId?: string;
  requesterName?: string;
  orderSerial: string;
  orderBudget: number;
  orderType: string;
  orderMethod: string;
  estDelivery: string | null;
  requestCommission: number;
  userType?: string;
  requestDate?: string;
}

const RequestsTable = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { search, page } = usePublicState();
  const filters = useOrdersFilters();
  const dispatch = useDispatch();

  const searchValue = search?.ordersReportSearch || '';

  const {
    data: ordersList,
    isLoading,
    isFetching,
  } = useGetOrdersReportQuery(
    {
      page: page,
      search: searchValue,
      dateFrom: filters?.fromDate,
      dateTo: filters?.toDate,
      orderMethod: filters?.orderMethod,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !filters?.fromDate && !filters?.toDate,
    }
  );

  const columns: TableProps<OrderReportType>['columns'] = [
    {
      title: t('reports.orderId', 'Order ID'),
      dataIndex: 'orderId',
      key: 'orderId',
    },
    {
      title: t('reports.orderBudget', 'Order budget'),
      dataIndex: 'orderBudget',
      key: 'orderBudget',
      render: (val) => val || '-',
    },

    {
      title: t('reports.requestComm', 'Request comm.'),
      dataIndex: 'comm',
      key: 'comm',
      render: (val) => val || '-',
    },
    {
      title: t('reports.requestDate', 'Request date'),
      key: 'dateCreated',
      dataIndex: 'dateCreated',
      render: (value) => {
        return (
          <p>
            {dayjs(value).isValid() ? dayjs(value).format('DD MMM YYYY') : '-'}
          </p>
        );
      },
    },
    {
      title: t('reports.estDelivery', 'Est. delivery'),
      dataIndex: 'estDelivery',
      key: 'estDelivery',
      render: (val) => val || '-',
    },
    {
      title: t('reports.orderType', 'Order type'),
      dataIndex: 'orderType',
      key: 'orderType',
      render: (val) => val || '-',
    },
    {
      title: t('reports.orderMethod', 'Order method'),
      dataIndex: 'orderMethod',
      key: 'orderMethod',
      render: (val) => val || '-',
    },
  ];

  dayjs.locale(language);

  return (
    <>
      <TableWrapper
        filters={<Filters />}
        title={t('reports.orders.orderList', 'Order list')}
        subTitle={t(
          'reports.orders.subTitle',
          'A detailed list of all platform orders'
        )}
      >
        <CustomTable
          rowKey="id"
          loading={isLoading || isFetching}
          currentPage={page || 0}
          pageSize={filters?.limit || 10}
          totalElements={ordersList?.payload?.totalElements || 0}
          onPageChange={(pageNumber, pageSize) => {
            dispatch(setPage(pageNumber - 1));
            dispatch(
              setOrdersFilters({
                limit: pageSize,
              })
            );
          }}
          onRowClick={(_, record: any) => {
            const id = record.id;
            dispatch(setModalId({ id }));
          }}
          columns={columns as any}
          dataSource={
            ordersList?.payload?.content?.map((item) => ({
              id: item?.orderId,
              requesterName: item?.requesterName,
              orderId: item.orderSerial,
              orderBudget: item.orderBudget,
              orderType: item.orderType,
              orderMethod: item.orderMethod,
              estDelivery: item.estDelivery,
              comm: item.requestCommission,
              userType: item?.userType,
              dateCreated: item?.requestDate,
            })) || []
          }
        />
      </TableWrapper>

      <RequestDetailsDrawer
        listOfIds={
          ordersList?.payload?.content?.map((el) =>
            String(el.orderId)
          ) as string[]
        }
      />
    </>
  );
};

export default RequestsTable;
